// ---------------------------------------------------------------------------
// Button, ported from the design system (R7)
//
// `secondary` is the default, `primary` the one decision a screen exists for,
// `ghost` for low-weight actions in dense places, `danger` for what cannot be
// resumed. With `href` it is a link that looks like the button: disabled or
// loading, it keeps its place, loses its href and leaves the tab order.
//
// React passes any other prop through to the element; here the attributes a
// caller sets are named (aria-*, title, id, name, value, type, tabIndex, and
// `autofocus` for the Dialog's data-autofocus).
// ---------------------------------------------------------------------------

import { html, nothing, type TemplateResult } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';

import { clickOnDisabled, cx, node } from './helpers.js';
import { type IconName, icon } from './icons.js';

export interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  icon?: IconName;
  iconRight?: IconName;
  /** Swaps the icon for a spinner, keeps the label, blocks a second click. */
  loading?: boolean;
  block?: boolean;
  disabled?: boolean;
  /** Renders a link instead of a button. */
  href?: string;
  type?: 'button' | 'submit' | 'reset';
  onClick?: (event: MouseEvent) => void;
  className?: string;
  id?: string;
  name?: string;
  value?: string;
  title?: string;
  tabIndex?: number;
  /** data-autofocus: the element a Dialog focuses first. */
  autofocus?: boolean;
  ariaLabel?: string;
  ariaExpanded?: boolean;
  ariaControls?: string;
  ariaHaspopup?: string;
  ariaPressed?: boolean;
}

const flag = (b: boolean | undefined) => (b == null ? undefined : String(b));

/** A button, or a link that looks like one. Without content it is icon-only and needs ariaLabel. */
export function button(p: ButtonProps, content?: unknown): TemplateResult {
  const variant = p.variant || 'secondary';
  const size = p.size || 'md';
  // Lit's nothing is the app's null (present() in helpers.ts).
  const iconOnly = content == null || content === nothing;
  const className = cx(
    'sdr-btn',
    `sdr-btn--${variant}`,
    `sdr-btn--${size}`,
    iconOnly && 'sdr-btn--icon',
    p.block && 'sdr-btn--block',
    p.className,
  );
  const lead = p.loading
    ? html`<span class="sdr-spinner" aria-hidden="true"></span>`
    : p.icon
      ? icon({ name: p.icon, size: size === 'lg' ? 18 : 16 })
      : null;
  const label = iconOnly ? null : html`<span>${node(content)}</span>`;
  const trail = p.iconRight ? icon({ name: p.iconRight, size: 16 }) : null;

  if (p.href != null) {
    const off = p.disabled || p.loading;
    return html`<a
      href=${ifDefined(off ? undefined : p.href)}
      class=${className}
      role=${ifDefined(off ? 'link' : undefined)}
      aria-disabled=${ifDefined(off ? 'true' : undefined)}
      aria-busy=${ifDefined(p.loading ? 'true' : undefined)}
      tabindex=${ifDefined(off ? -1 : p.tabIndex)}
      id=${ifDefined(p.id)}
      title=${ifDefined(p.title)}
      data-autofocus=${ifDefined(p.autofocus ? '' : undefined)}
      aria-label=${ifDefined(p.ariaLabel)}
      aria-expanded=${ifDefined(flag(p.ariaExpanded))}
      aria-controls=${ifDefined(p.ariaControls)}
      aria-haspopup=${ifDefined(p.ariaHaspopup)}
      aria-pressed=${ifDefined(flag(p.ariaPressed))}
      @click=${p.onClick}
    >${lead}${label}${trail}</a>`;
  }
  // React never calls a disabled button's onClick, also for a click on its icon or label (clickOnDisabled)
  const click = p.onClick;
  const onClick =
    click &&
    function (this: unknown, e: MouseEvent) {
      if (!clickOnDisabled(e)) click.call(this, e);
    };
  return html`<button
    type=${p.type || 'button'}
    class=${className}
    ?disabled=${p.disabled || p.loading}
    aria-busy=${ifDefined(p.loading ? 'true' : undefined)}
    tabindex=${ifDefined(p.tabIndex)}
    id=${ifDefined(p.id)}
    name=${ifDefined(p.name)}
    value=${ifDefined(p.value)}
    title=${ifDefined(p.title)}
    data-autofocus=${ifDefined(p.autofocus ? '' : undefined)}
    aria-label=${ifDefined(p.ariaLabel)}
    aria-expanded=${ifDefined(flag(p.ariaExpanded))}
    aria-controls=${ifDefined(p.ariaControls)}
    aria-haspopup=${ifDefined(p.ariaHaspopup)}
    aria-pressed=${ifDefined(flag(p.ariaPressed))}
    @click=${onClick}
  >${lead}${label}${trail}</button>`;
}
